/**
 * @file ItemSupplierRow.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-02
 * @modified 2026-11-02
 * @description The item window's Supplier row (Cam, 30 Oct / 2 Nov 2026):
 *              who the item is bought from — a supplier, a supplier group
 *              ("Mole Valley Feed Solutions group — any depot, picked on each
 *              order", its depots under it) or none — and "Change supplier…"
 *              (ItemSupplierPicker in a small window; GET / PUT
 *              /api/inventory/:id/supplier). A depot chosen is saved as its
 *              group, and the window says so. Local only — never sent to
 *              Xero. "Not ready" before migrations/item_supplier.sql; greyed
 *              with why without 'Edit inventory items'.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import ItemSupplierPicker from './ItemSupplierPicker';
import { useSlideOutButton } from './SlideOutButton';
import useItemSupplierOptions from '../hooks/useItemSupplierOptions';
import { NOT_READY_REASON } from '../utils/itemSupplier';
import './ItemSupplier.css';

const errorOf = async (res, fallback) => {
  const d = await res.json().catch(() => ({}));
  return d.error || fallback;
};

function ChangeSupplierWindow({ itemId, current, onClose, onSaved }) {
  const lists = useItemSupplierOptions();
  const [choice, setChoice] = useState(current?.key || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [said, setSaid] = useState(null);
  useSlideOutButton([saving, choice, said]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/inventory/${itemId}/supplier`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ supplier: choice || null })
      });
      if (!res.ok) throw new Error(await errorOf(res, 'Could not change the supplier'));
      const data = await res.json();
      // a depot saved as its group: say so before closing
      if (data.message) { setSaid(data.message); onSaved(false); return; }
      onSaved(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="md" className="item-supplier" title="Change supplier"
      footer={
        <div className="buttons slideout is-footer">
          <div className="is-footer-gap" />
          <div>
            <button className="btn-slideout" onClick={onClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">{said ? 'check' : 'close'}</span></div>
              <span className="btn-label">{said ? 'Done' : 'Cancel'}</span>
            </button>
          </div>
          {!said && (
            <div>
              <button className="btn-slideout" data-write-action onClick={save}
                disabled={saving || !lists.loaded || lists.ready === false}
                title={lists.ready === false ? (lists.reason || NOT_READY_REASON) : 'Save'}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
                <span className="btn-label">{saving ? 'Saving…' : 'Save'}</span>
              </button>
            </div>
          )}
        </div>
      }>
      <div className="is-window">
        <label className="is-field">
          <span>Supplier</span>
          <ItemSupplierPicker value={choice} onChange={setChoice} lists={lists} current={current}
            disabled={!!said || !lists.loaded || lists.ready === false}
            placeholder={lists.loaded ? 'Choose the supplier…' : 'Loading suppliers…'} />
        </label>
        <p className="is-hint">
          Fills in the supplier when this item is picked on an order, invoice, PO or bill (when none is chosen yet), and
          pins it at the top of the supplier list. A supplier group fills nothing — its depots are pinned and one is picked
          on each order. Kept here only — never sent to Xero.
        </p>
        {said && <p className="is-hint" role="status">{said}</p>}
        {error && <p className="is-error" role="alert">{error}</p>}
      </div>
    </Modal>
  );
}

function ItemSupplierRow({ itemId }) {
  const [state, setState] = useState(null);
  const [changing, setChanging] = useState(false);

  const load = useCallback(async () => {
    if (!itemId) return;
    try {
      const res = await fetch(`${API_URL}/api/inventory/${itemId}/supplier`, { credentials: 'include' });
      setState(res.ok ? await res.json() : { error: await errorOf(res, 'Could not read the supplier') });
    } catch {
      setState({ error: 'Could not read the supplier' });
    }
  }, [itemId]);

  useEffect(() => { load(); }, [load]);

  if (!state) return null;
  const ready = state.ready !== false;
  const s = state.supplier;
  const block = !ready ? (state.reason || NOT_READY_REASON) : (state.edit_block || null);

  return (
    <div className="detail-row is-item-row">
      <div className="detail-group">
        <label>Supplier</label>
        <div className="detail-value is-item-value">
          {!ready ? <span className="is-muted">{block}</span>
            : state.error ? <span className="is-muted">{state.error}</span>
              : s ? (
                <>
                  <span>{s.text}</span>
                  {s.archived && <span className="archived-badge">Archived</span>}
                  {s.kind === 'group' && (
                    <span className="is-members" title={(s.members || []).map(m => m.name).join(', ')}>
                      Any depot — picked on each order
                    </span>
                  )}
                </>
              ) : <span className="is-muted">No supplier</span>}
          {ready && !state.error && (
            <button className="btn-slideout" data-write-action onClick={() => setChanging(true)} disabled={!!block}
              title={block || 'Choose who this item is bought from'}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">local_shipping</span></div>
              <span className="btn-label">Change supplier…</span>
            </button>
          )}
        </div>
      </div>
      {changing && (
        <ChangeSupplierWindow itemId={itemId} current={s}
          onClose={() => { setChanging(false); load(); }}
          onSaved={(close) => { load(); if (close) setChanging(false); }} />
      )}
    </div>
  );
}

export default ItemSupplierRow;
